import React, { lazy, Suspense, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import LazyImage from 'components/UI/LazyImage';
import Loader from 'components/UI/Loader';
import Steps from './Steps';
import { clients as defaultClients } from './constants';
import { useTrans, useLang, useIsUSA } from 'utils/localisation';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './ContactUs.module.scss';

const CorporateForm = lazy(() => import('components/Forms/CorporateForm/CorporateForm'));

function ContactUs({
  formId = 'get-consultation',
  formName,
  title,
  clients: customClients,
  stepsTitle,
  steps,
  description,
  commentPlaceholder,
  commentPlaceholderMessage,
  withSteps = true,
  logosTitle,
  withClients = true,
  submitTitle,
  withAttach,
}) {
  const t = useTrans(['common', 'getConsultation']);
  const { ref, inView } = useInView({
    triggerOnce: true,
  });
  const lang = useLang();
  const isUSA = useIsUSA();

  const preparedClients = useMemo(() => {
    if (lang === 'en' && isUSA) {
      return defaultClients.us;
    }

    return defaultClients[lang] ?? defaultClients.en;
  }, [lang, defaultClients, isUSA]);

  const clients = customClients ?? preparedClients;

  return (
    <Container id="get-consultation" padding="Vertical" className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.column}>
          <Title tag="h2" size="h3">
            {title ?? t('contactUsTitle')}
          </Title>
          {description && <Subtitle size="lg">{description}</Subtitle>}
          {withSteps && (
            <Steps
              title={stepsTitle ?? t('processStepsTitle')}
              steps={steps ?? t('processSteps')}
              className={styles.steps}
            />
          )}
          {withClients && (
            <div className={styles.clients}>
              <Subtitle size="lg">{logosTitle ?? t('clientsWhoTrustUs')}</Subtitle>
              <div className={styles.clientsList}>
                {clients.map(({ icon, alt }) => (
                  <LazyImage
                    key={icon}
                    src={icon}
                    className={styles.clientLogo}
                    alt={alt ?? 'Icon'}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div ref={ref} className={styles.column}>
          <div className={styles.mobileTitles}>
            <Title tag="h2">{title ?? t('contactUsTitle')}</Title>
            {description && <Subtitle size="lg">{description}</Subtitle>}
          </div>
          {inView && (
            <Suspense fallback={<Loader theme="block" />}>
              <CorporateForm
                id={formId}
                className={styles.form}
                submitTitle={submitTitle ?? t('sendRequest')}
                formName={formName ?? 'get-consultation'}
                commentPlaceholder={commentPlaceholder}
                commentPlaceholderMessage={commentPlaceholderMessage}
                withAttach={withAttach}
              />
            </Suspense>
          )}
        </div>
      </div>
    </Container>
  );
}

ContactUs.displayName = 'ContactUs';

export default withErrorBoundary(ContactUs);
